<template>
  <a-modal
    :visible="visible"
    :footer="null"
    :closable="true"
    :width="400"
    @cancel="$emit('close')"
    centered
    class="session-detail-modal"
  >
    <div class="p-2">
      <!-- Title -->
      <h2 class="text-xl font-bold text-[#2F3E4C] mb-4 pr-6">
        {{ sessionData.codigo_queja }} - {{ sessionTypeTitle }}
      </h2>

      <!-- Date and Time -->
      <div class="mb-4 text-left detail-fields-grid" style="font-size: 14px">
        <div class="w-6 h-6 flex items-center justify-center bg-white rounded-lg self-center">
          <a-icon
            type="calendar"
            class="text-[#2F3E4C] text-lg leading-none"
          />
        </div>
        <span class="font-bold text-gray-700 self-center">Fecha:</span>
        <span class="text-gray-600 capitalize self-center">{{
          formattedDate
        }}</span>

        <div class="w-6 h-6 flex items-center justify-center bg-white rounded-lg self-center">
          <a-icon
            type="clock-circle"
            class="text-[#2F3E4C] text-lg leading-none"
          />
        </div>
        <span class="font-bold text-gray-700 self-center">Hora:</span>
        <span class="text-gray-600 self-center">{{ formattedTime }}</span>

        <!-- Status -->
        <div class="w-6 h-6 flex items-center justify-center bg-white rounded-lg self-center">
          <a-icon
            type="profile"
            class="text-[#2F3E4C] text-lg leading-none"
          />
        </div>
        <span class="font-bold text-gray-700 self-center">Estado:</span>
        <span
          class="font-bold self-center"
          :style="{ color: statusColor }"
          >{{ sessionData.session_state_name }}</span
        >

        <template
          v-if="sessionData.session_state_name === STATE_SESION_FINALIZADA || sessionData.session_state_name === STATE_SESION_FINALIZADA_SIN_GESTION"
        >
          <!-- Enlace sesión -->
          <div class="w-6 h-6 flex items-start justify-center bg-white rounded-lg self-start">
            <a-icon type="link" class="text-[#2F3E4C] text-lg leading-none" />
          </div>
          <span class="font-bold text-gray-700 self-start">Sesión:</span>
          <a
            :href="sessionData.session_link"
            target="_blank"
            rel="noopener noreferrer"
            class="font-bold text-blue-600 hover:underline break-all self-start"
          >
            {{ sessionData.session_link }}
          </a>
        </template>
      </div>

      <!-- Participants -->
      <div class="space-y-4 mb-8">
        <h3 class="font-bold text-base text-[#2F3E4C]">Participantes</h3>

        <!-- Financial Consumer -->
        <div v-if="sessionData.cf">
          <p class="font-bold text-sm text-gray-800">Consumidor financiero</p>
          <p class="text-sm text-gray-600">
            {{ sessionData.cf.nombre_completo || sessionData.nameCf }}
          </p>
          <p class="text-sm text-gray-500">
            {{
              sessionData.cf.correo_electronico ||
              (sessionData.cf ? sessionData.cf.email : "")
            }}
          </p>
        </div>

        <!-- Supervised Entity -->
        <div v-if="sessionData.ev">
          <p class="font-bold text-sm text-gray-800">Entidad vigilada</p>
          <p class="text-sm text-gray-600">
            {{ sessionData.ev.razon_social || sessionData.nameEv }}
          </p>
          <p class="text-sm text-gray-500">
            {{
              sessionData.ev.correo_electronico ||
              (sessionData.ev ? sessionData.ev.email : "")
            }}
          </p>
        </div>

        <!-- Facilitator -->
        <div v-if="sessionData.facilitador">
          <p class="font-bold text-sm text-gray-800">Facilitador</p>
          <p class="text-sm text-gray-600">
            {{ sessionData.facilitador.nombre_completo }}
          </p>
          <p class="text-sm text-gray-500">
            {{ sessionData.facilitador.correo_electronico }}
          </p>
        </div>

        <template
          v-if="
            sessionData.session_state_name === STATE_SESION_PENDIENTE_AGENDAR ||
            sessionData.session_state_name ===
              STATE_SESION_PENDIENTE_ATENDER_REPROGRAMACION
          "
        >
          <p class="font-bold text-sm text-gray-800">Enlace de sesión</p>
          <BaseInput
            type="text"
            v-model="sessionLink"
            placeholder="https://example.gov.co/session-link"
          />
        </template>

        <p v-if="feedback" class="text-sm text-center text-red-500">
          {{ feedback }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex flex-col gap-3">
        <template
          v-if="sessionData.session_state_name === STATE_SESION_CHAT_AGENDADA"
        >
          <BaseButton
            @click.native="handleAccessSession"
            label="ACCEDER A LA SESIÓN"
            class="w-full mb-2"
            :append="true"
          />

          <BaseButton
            v-if="sessionData.has_sfc_reschedule === false"
            @click.native="handleRescheduleSession"
            label="REPROGRAMAR SESIÓN"
            class="w-full"
            :invert="true"
          />
        </template>
        <template
          v-if="sessionData.session_state_name === STATE_SESION_FINALIZADA || sessionData.session_state_name === STATE_SESION_FINALIZADA_SIN_GESTION"
        >
          <BaseButton
            @click.native="handleDetailComplaint"
            label="VER DETALLE DE LA QUEJA"
            class="w-full mb-2"
            :append="true"
          />
        </template>
        <template
          v-if="
            sessionData.session_state_name === STATE_SESION_PENDIENTE_AGENDAR ||
            sessionData.session_state_name ===
              STATE_SESION_PENDIENTE_ATENDER_REPROGRAMACION
          "
        >
          <BaseButton
            @click.native="handleScheduleSession"
            label="AGENDAR SESIÓN"
            class="w-full mb-2"
            :append="true"
          />
        </template>
      </div>
    </div>
  </a-modal>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import {
  STATE_SESION_PENDIENTE_AGENDAR,
  STATE_SESION_CHAT_AGENDADA,
  STATE_SESION_PENDIENTE_ATENDER_REPROGRAMACION,
  STATE_SESION_FINALIZADA,
  STATE_SESION_FINALIZADA_SIN_GESTION,
  SFC_COLOR_AMARILLO,
  SFC_COLOR_AZUL,
  SFC_COLOR_VERDE,
  SFC_COLOR_NARANJA,
  SFC_COLOR_AZUL_CELESTE,
  SFC_COLOR_GRIS,
} from "@/core/utils/constants";
import ErrorMessaje from "@/core/error/error_messaje";

export default {
  name: "SessionDetailModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    sessionData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      feedback: "",
      sessionLink: "",
      STATE_SESION_CHAT_AGENDADA,
      STATE_SESION_FINALIZADA,
      STATE_SESION_FINALIZADA_SIN_GESTION,
      STATE_SESION_PENDIENTE_AGENDAR,
      STATE_SESION_PENDIENTE_ATENDER_REPROGRAMACION,
    };
  },
  methods: {
    checkForm() {
      this.feedback = "";
      let valid = false;

      if (this.sessionLink === "") {
        this.feedback =
          "Para agendar una sesión, debe ingresar el enlace de acceso de la sesión";
        return valid;
      }

      try {
        new URL(this.sessionLink);
      } catch (error) {
        this.feedback = "Por favor, ingrese un enlace válido";
        return valid;
      }

      valid = true;
      this.feedback = "";
      return valid;
    },
    handleAccessSession() {
      // Emitir evento para acceder a la sesión
      this.$emit("access");
    },
    handleDetailComplaint() {
      // Emitir evento para acceder a la sesión
      this.$emit("goToComplaintDetail", this.sessionData);
    },
    handleRescheduleSession() {
      // Emitir evento para reprogramar la sesión
      this.$emit("reschedule");
    },
    handleScheduleSession() {
      if (this.checkForm()) {
        // Emitir evento para agendar una sesión
        this.$emit("schedule", this.sessionLink);
      }
    },
  },
  computed: {
    sessionTypeTitle() {
      // Podría variar según el tipo de queja, por ahora "Sesión de mediación" según imagen
      return "Sesión de mediación";
    },
    formattedDate() {
      if (!this.sessionData.session_date_original) return "";
      // Formato: Martes, 26 de Enero 2025
      return moment(this.sessionData.session_date_original)
        .locale("es")
        .format("dddd, D [de] MMMM YYYY");
    },
    formattedTime() {
      if (
        !this.sessionData.session_start_time ||
        !this.sessionData.session_end_time
      )
        return "";

      const formatTime = (timeStr) => {
        // Asumiendo que viene en formato HH:mm:ss o HH:mm
        return moment(timeStr, "HH:mm:ss").format("h:mm");
      };

      const start = formatTime(this.sessionData.session_start_time);
      const end = formatTime(this.sessionData.session_end_time);
      const ampm = moment(this.sessionData.session_end_time, "HH:mm:ss").format(
        "a",
      );

      return `${start} - ${end} ${ampm === "am" ? "a.m." : "p.m."}`;
    },
    statusColor() {
      switch (this.sessionData.session_state_name) {
        case STATE_SESION_CHAT_AGENDADA:
          return SFC_COLOR_AZUL;
        case STATE_SESION_FINALIZADA:
          return SFC_COLOR_VERDE;
        case STATE_SESION_FINALIZADA_SIN_GESTION:
          return SFC_COLOR_NARANJA;
        case STATE_SESION_PENDIENTE_AGENDAR:
        case STATE_SESION_PENDIENTE_ATENDER_REPROGRAMACION:
          return SFC_COLOR_AMARILLO;
        default:
          return "#2F3E4C";
      }
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.sessionLink = "";
        this.feedback = "";
      }
    },
    sessionLink() {
      this.feedback = "";
    },
  },
};
</script>

<style scoped>
.detail-fields-grid {
  display: grid;
  grid-template-columns: 1.5rem 4rem 1fr;
  column-gap: 0.75rem;
  row-gap: 0.5rem;
  align-items: start;
}
:deep(.ant-modal-content) {
  border-radius: 12px;
}
:deep(.ant-modal-close) {
  top: 10px;
  right: 10px;
}
:deep(.ant-modal-close-x) {
  font-size: 20px;
  font-weight: bold;
  color: #000;
}
.rounded-full {
  border-radius: 9999px !important;
}
</style>
